import { apiConfig } from '../config/api.config';

export interface AuthTokenValidationResult {
	success: boolean;
	error?: string;
}

/**
 * Validates a cross-domain auth token by calling the backend validation endpoint.
 * This function is called before the React app mounts to establish a session.
 *
 * @param token - The auth token from the URL query parameter
 * @returns Promise resolving to validation result
 */
export async function validateAuthToken(token: string): Promise<AuthTokenValidationResult> {
	try {
		const validateUrl = `${apiConfig.identityApiBaseUrl}/auth/cross-domain/validate`;

		const response = await fetch(validateUrl, {
			method: 'POST',
			credentials: 'include', // Include cookies for session establishment
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ token }),
		});

		if (response.ok) {
			return { success: true };
		}

		// Handle non-200 responses
		const errorMessage = `Validation failed with status ${response.status}`;
		console.error('Auth token validation failed:', errorMessage);
		return { success: false, error: errorMessage };
	} catch (error) {
		// Handle network errors or other exceptions
		const errorMessage = error instanceof Error ? error.message : 'Unknown error';
		console.error('Auth token validation error:', errorMessage);
		return { success: false, error: errorMessage };
	}
}

/**
 * Extracts the auth token from the current URL fragment.
 *
 * @returns The auth token if present, null otherwise
 */
export function getAuthTokenFromUrl(): string | null {
	// see circadia-colleague-frontend\src\components\Navigation\MainNavBar\MainNavBar.tsx:30 for source
	const params = new URLSearchParams(window.location.hash.substring(1));
	return params.get('authToken');
}

/**
 * Removes the auth token from the URL without reloading the page.
 * Uses history.replaceState to clean the URL.
 */
export function removeAuthTokenFromUrl(): void {
	const url = new URL(window.location.href);
	url.searchParams.delete('authToken');
	window.history.replaceState({}, '', url.pathname + url.search);
}
